// api
import type {
  GetEnergyTreeNodeDto,
  PutEnergyTreeNodeDto,
  SpecialNodeType,
} from '@/api/generated/recalc/endpoints/v2/energyGroups';

// relative
import type { EditNodeFormValues } from './editNodeForm.schema';

const getDefaultOpeningHours = () => ({
  monday: [{ start: 7.0, end: 17.0 }],
  tuesday: [{ start: 7.0, end: 17.0 }],
  wednesday: [{ start: 7.0, end: 17.0 }],
  thursday: [{ start: 7.0, end: 17.0 }],
  friday: [{ start: 7.0, end: 17.0 }],
  saturday: [],
  sunday: [],
});

const DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const normalizeOpeningHours = (openingHours) => {
  const base = getDefaultOpeningHours();

  // If nothing is provided, use defaults
  if (!openingHours || typeof openingHours !== 'object') return base;

  // Ensure all days exist and are arrays
  const normalized = { ...base };
  for (const day of DAYS) {
    const v = openingHours[day];
    normalized[day as keyof typeof normalized] = Array.isArray(v) ? v : [];
  }

  // Optional: sanitize entries (keep only numeric start/end)
  for (const day of DAYS) {
    const dayKey = day as keyof typeof normalized;
    normalized[dayKey] = (normalized[dayKey] || [])
      .filter(
        (x) => x && typeof x.start === 'number' && typeof x.end === 'number',
      )
      .map((x) => ({ start: x.start, end: x.end }));
  }

  return normalized;
};

export function toEditNodeFormValues(
  dto?: GetEnergyTreeNodeDto,
): EditNodeFormValues {
  return {
    name: dto?.name ?? '',
    type: dto?.type ?? '',
    dataStreams: dto?.dataStreams ?? [],
    options: {
      // Convert 0, "", or any falsy value to null for non-residual nodes
      specialNodeType: dto?.options?.specialNodeType || null,
      consumption: dto?.options?.consumption ?? null,
      percentage: dto?.options?.percentage ?? null,
      openingHours: normalizeOpeningHours(dto?.options?.openingHours),
      goShares: Array.isArray(dto?.options?.goShares)
        ? dto.options.goShares
        : [],
    },
    locationId: dto?.locationId ?? null,
  };
}

export function toPutEnergyTreeNodeDto(
  values: EditNodeFormValues,
): PutEnergyTreeNodeDto {
  return {
    name: values.name?.trim() || null,
    type: values.type || null,
    dataStreams: values.dataStreams,
    options: {
      specialNodeType:
        (values.options.specialNodeType as SpecialNodeType | null) || null,
      consumption: values.options.consumption,
      percentage: values.options.percentage,
      openingHours: values.options.openingHours || null,
      goShares: values.options.goShares || null,
    },
    locationId: values.locationId,
  };
}
